import EmptyState from '@/components/EmptyState';
import HorizontalDatePicker from '@/components/HorizontalDatePicker';
import { paymentMethodLabel } from '@/features/orders/utils/payment-method';
import { MetricCard } from '@/features/reports/components/MetricCard';
import { ReportCard, ReportRow } from '@/features/reports/components/ReportCard';
import { useDayEnd } from '@/features/reports/hooks/useReports';
import { todayLocal } from '@/features/reports/lib/range';
import { featureFlags } from '@/featureFlags';
import { useT } from '@/i18n';
import { useRestaurantTimezone } from '@/stores/restaurant.store';
import { formatMoney } from '@/utils/money';
import { Block, List, ListInput, Preloader } from 'framework7-react';
import { useState } from 'react';

/**
 * The Z-report for ONE day, with cash-up.
 *
 * Deliberately keeps its own day picker instead of using the page's period bar:
 * a day-end is a single-day close-out, so a multi-day range has no meaning here.
 */
export function DayEndSection() {
  const t = useT();
  const de = t.reports.dayEnd;
  const dineInEnabled = featureFlags.tableOrdering;
  const tz = useRestaurantTimezone();
  const [date, setDate] = useState(() => todayLocal(tz));
  const [counted, setCounted] = useState('');
  const { data: report, isLoading, error } = useDayEnd(date);

  const cur = report?.currency ?? 'USD';
  const countedCents = counted.trim() ? Math.round(parseFloat(counted) * 100) : null;
  const delta = countedCents !== null && report ? countedCents - report.expectedCashCents : null;
  const takenBy = report?.byMethod.filter((m) => m.count > 0) ?? [];

  /**
   * Why takings ≠ the Revenue tab's net sales for the same day.
   *
   * Net sales carries everything the restaurant keeps (items, delivery, service
   * charge) with refunds already taken off; takings is what was actually
   * charged, so it still has those refunds in it. Owners hit that gap doing a
   * cash count and read it as a broken report, so the day-close spells the
   * bridge out instead of leaving two different numbers on two tabs.
   *
   * The last row is the residual, not a computed component: payments are dated
   * by when they were TAKEN and orders by when they were PLACED, so a tab opened
   * before midnight and settled after it lands on different days and the parts
   * genuinely won't close. Naming the leftover keeps the card honest rather than
   * printing a sum that doesn't add up.
   */
  const bridge = (() => {
    if (!report || report.takingsCents === report.netSalesCents) return null;
    const parts = [
      { key: 'net', label: de.netSales, cents: report.netSalesCents },
      { key: 'refunds', label: de.refundsAddedBack, cents: report.refundsCents },
    ].filter((p) => p.key === 'net' || p.cents !== 0);
    const residual = report.takingsCents - parts.reduce((a, p) => a + p.cents, 0);
    return residual === 0
      ? parts
      : [...parts, { key: 'other', label: de.otherDifference, cents: residual }];
  })();

  return (
    <>
      {/* The shared date strip (same control as Orders history and the
          reservations bar), not a bespoke picker - it already handles the
          infinite scroll, centring and month popover. */}
      <div className="rpt-daystrip">
        <HorizontalDatePicker selectedDate={date} onDateSelect={setDate} />
      </div>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={de.couldntLoad}
          description={(error as Error).message}
        />
      )}

      {report && (
        <>
          <div className={dineInEnabled ? 'rpt-kpis rpt-kpis-6' : 'rpt-kpis'}>
            <MetricCard label={de.netReceived} value={formatMoney(report.netReceivedCents, cur)} />
            <MetricCard label={de.takings} value={formatMoney(report.takingsCents, cur)} />
            {/* No "−" on a zero - "−€0.00" reads as a broken value. */}
            <MetricCard
              label={de.refunds}
              value={
                report.refundsCents > 0
                  ? `−${formatMoney(report.refundsCents, cur)}`
                  : formatMoney(0, cur)
              }
            />
            <MetricCard label={de.vat} value={formatMoney(report.vatCents, cur)} />
            {/* Tips come only from the dine-in take-payment flow, so this is a
                permanent 0.00 until table ordering ships. */}
            {dineInEnabled && (
              <MetricCard label={de.tipsPayout} value={formatMoney(report.tipsCents, cur)} />
            )}
          </div>

          {report.openSessionsAtClose > 0 && (
            <div className="rpt-row">
              <ReportCard className="rpt-wide">
                <div style={{ color: '#a15c00', fontSize: 14 }}>
                  {de.openTabsWarning(
                    report.openSessionsAtClose,
                    report.openSessionsAtClose === 1 ? de.tabPlural : de.tabsPluralForm,
                  )}
                </div>
              </ReportCard>
            </div>
          )}

          {bridge && (
            <div className="rpt-row">
              <ReportCard className="rpt-wide" title={de.reconciliation}>
                {bridge.map((p, i) => (
                  <ReportRow
                    key={p.key}
                    label={p.label}
                    // Every row after the first is an addition to net sales, so
                    // it carries its sign - a bare "9.00" under "294.00" reads
                    // as a second total rather than a component of one.
                    value={
                      i === 0
                        ? formatMoney(p.cents, cur)
                        : `${p.cents < 0 ? '−' : '+'}${formatMoney(Math.abs(p.cents), cur)}`
                    }
                    style={i === 0 ? { borderTop: 'none' } : undefined}
                  />
                ))}
                <div
                  style={{
                    marginTop: 4,
                    paddingTop: 9,
                    borderTop: '1px solid rgba(142,142,147,0.4)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  <span>{de.takings}</span>
                  <span>{formatMoney(report.takingsCents, cur)}</span>
                </div>
              </ReportCard>
            </div>
          )}

          <div className="rpt-row">
            {/* Only render the breakdown when something was actually taken -
                an empty titled card reads as a load failure. */}
            {takenBy.length > 0 && (
              <ReportCard title={de.byPaymentMethod}>
                {takenBy.map((m, i) => (
                  <ReportRow
                    // Two gateways share methodKind 'ONLINE' — key on the brand too.
                    key={`${m.methodKind}:${m.providerName ?? ''}`}
                    label={m.providerName ?? paymentMethodLabel(m.methodKind, t.orders.methodKind)}
                    value={formatMoney(m.netCents, cur)}
                    style={i === 0 ? { borderTop: 'none' } : undefined}
                  />
                ))}
              </ReportCard>
            )}

            {/* Alone in the row when nothing was taken, so it spans rather than
                sitting as a lone third-width card. */}
            <ReportCard className={takenBy.length > 0 ? undefined : 'rpt-wide'} title={de.cashUp}>
              <ReportRow
                label={de.expectedInDrawer}
                value={formatMoney(report.expectedCashCents, cur)}
                style={{ borderTop: 'none' }}
              />
              <List strongIos style={{ margin: '4px 0 0' }}>
                <ListInput
                  label={de.counted}
                  type="number"
                  inputmode="decimal"
                  placeholder={de.countedPlaceholder}
                  value={counted}
                  onInput={(e) => setCounted((e.target as HTMLInputElement).value)}
                />
              </List>
              {delta !== null && (
                <div
                  style={{
                    marginTop: 8,
                    fontWeight: 700,
                    color: delta === 0 ? '#248a3d' : '#c9372c',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>{delta === 0 ? de.balanced : delta > 0 ? de.over : de.short}</span>
                  <span>{formatMoney(Math.abs(delta), cur)}</span>
                </div>
              )}
            </ReportCard>
          </div>
        </>
      )}
    </>
  );
}
